<template>
  <div>
    <div class="bg-blue-light p-2">
      <DateQuerySetter
        :inEnoughSpace="false"
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="card mt-1 p-2">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Voucher Create Date</th>
              <th>Voucher No</th>
              <th>Party Details</th>
              <th>Item / Account Head</th>
              <th class="text-end">Amount</th>
              <th>Attachment</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="! rows.length">
              <td colspan="8" class="text-center">No clearance data found.</td>
            </tr>
            <tr v-for="(item, i) in rows" :key="i">
              <td>{{ item.date }}</td>
              <td>{{ item.voucher_no }}</td>
              <td>{{ item.party_name }}</td>
              <td>{{ item.account_head_display }}</td>
              <td class="text-end">{{ commaFormat(item.amount) }}</td>
              <td>
                <button
                  v-if="item.attachments && item.attachments.length"
                  class="btn btn-outline-secondary btn-sm"
                  title="View attachment"
                  @click="viewAttachments(item.attachments)"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
              </td>
              <td>
                <span>{{ item.status }}</span>
              </td>
              <td>
                <button class="btn btn-primary btn-sm" @click="onClickClearance(item)">Clearance</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        :offset="offset"
        @onPageChange="onPageChange"
        ref="paginationRef"
      />

      <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :isFilterable="true"
        :companyRoles="companyRoles"
      />
    </div>

    <MakePaymentModal
      v-if="modalOpen && activeLedgerId"
      :ledger-master-id="activeLedgerId"
      :force-clear="true"
      @close="onCloseModal"
      @success="fetchList"
    />

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import MakePaymentModal from './MakePaymentModal.vue'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import handleContact from '@/services/modules/contact'
import handleRole from '@/services/modules/role'
import figureFormatter from '@/services/utils/figureFormatter'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'

const showError = inject('showError')
const store = useStore()
const route = useRoute()
const router = useRouter()

const { fetchPaymentRequisitionList } = handlePaymentRequisition()
const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchCompanyDefaultRoles } = handleRole()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const rows = ref([])
const contactProfile = ref(null)
const companyRoles = ref([])
const offset = ref(20)
const paginationRef = ref(null)
const modalOpen = ref(false)
const activeLedgerId = ref(null)

const companyId = computed(() => route.params.companyId)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
  query += '&status=pa_approved'
  query += '&cheque_status=paid'
  query += '&offset=' + offset.value
  return query
}

const setPagination = (data) => {
  if (! data) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const fetchList = async () => {
  loading.value = true
  rows.value = []
  try {
    const res = await fetchPaymentRequisitionList(getQuery())
    if (! res.status) {
      store.commit('resetPagination')
      return
    }
    rows.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const onClickGo = async () => {
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery })
  await fetchList()
}

const onPageChange = async (p) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = p
  await router.push({ path: route.path, query: routeQuery })
  await fetchList()
}

const onClickClearance = (item) => {
  activeLedgerId.value = item.ledger_master_id
  modalOpen.value = true
}

const onCloseModal = () => {
  modalOpen.value = false
  activeLedgerId.value = null
}

const viewAttachments = (attachments) => {
  if (! attachments || ! attachments.length) return
  window.open(attachments[0].path, '_blank')
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}`)
  if (! res.status) return
  companyRoles.value = res.data
}

onMounted(() => {
  store.commit('resetPagination')
  getCompanyRoles()
  fetchList()
})
</script>

<style scoped>
.filter-label {
  margin-bottom: 4px;
}
</style>
